<template>
  <BaseModal
    v-model="isOpen"
    :title="modalTitle"
    :close-on-backdrop="true"
    :show-close-icon="true"
    :no-scroll="showExtendEta"
    size="lg"
    @close="onClosed"
    @open="onOpened"
  >
    <template #default>
      <AppLoading />
      <span>
        <transition name="fade">
          <ExtendEta
            v-show="showExtendEta"
            :idProject="project?.id"
            @close="closeExtendEta"
          />
        </transition>
      </span>
      <span>
        <transition name="fade">
          <EstimatedWorkHoursModal
            v-show="showEstimatedWorkHoursModal"
            :idProject="project?.id"
            @close="closeEstimatedWorkHoursModal"
          />
        </transition>
      </span>
      <span>
        <transition name="fade">
          <CancelProjectModal
            v-show="showCancelProjectModal"
            :projectData="project"
            @close="closeCancelProjectModal"
          />
        </transition>
      </span>

      <h5>{{ project?.eta }}</h5>

      <button class="btn btn-outline-warning me-2" @click="openDriveFolder">
        Drive
      </button>

      <button
        v-show="
          project &&
          !['No Autorizado', 'Cancelado', 'Terminado'].includes(project.state)
        "
        class="btn btn-outline-light"
        @click="openExtendEta"
      >
        Extender Eta
      </button>

      <button
        class="btn btn-outline-warning ms-2"
        @click="openEstimatedWorkHoursModal"
      >
        Estimación de horas
      </button>

      <button
        class="btn btn-outline-danger ms-2"
        @click="openCancelProjectModal"
      >
        Cancelar Proyecto
      </button>

      <div class="contenedor">
        <div
          v-for="(audit, index) in reversedAudited"
          :key="index"
          class="line"
        >
          <div class="box" :class="{ expanded: showInfo[index] }">
            <div class="inline-title">
              <h5>{{ audit.title }}</h5>
              <button @click="expandBox(index)" class="btn btn-outline-light">
                ▼
              </button>
            </div>

            <transition name="abajos">
              <div v-if="showInfo[index]">
                <div class="inline-title">
                  <h5>Usuario:</h5>
                  <p>{{ audit.user_name }}</p>
                </div>
                <div class="col-md-12" v-html="formatAuditBody(audit)"></div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button class="btn btn-outline-light" @click="closePopUp">Cerrar</button>
    </template>
  </BaseModal>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  defineOptions,
  defineProps,
  defineEmits,
} from "vue";
import { useStore } from "vuex";
import BaseModal from "./BaseModal.vue";
import ExtendEta from "./extendEta.vue";
import EstimatedWorkHoursModal from "./EstimatedWorkHoursModal.vue";
import CancelProjectModal from "./CancelProjectModal.vue";
import AppLoading from "./AppLoading.vue";

defineOptions({ name: "HistoryModal" });

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  idProject: { type: [Number, String], required: false },
});

const emit = defineEmits(["update:modelValue", "close", "open"]);

const store = useStore();
const project = computed(() => store.state.project.project);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const modalTitle = computed(() => {
  if (!project.value) return "";
  const code = project.value.project_code || "";
  const name = project.value.name || "";
  return `${code}_${name}`;
});

const showExtendEta = ref(false);
const showEstimatedWorkHoursModal = ref(false);
const showCancelProjectModal = ref(false);
const showInfo = ref([]);

const reversedAudited = computed(() => {
  return project.value?.audited ? project.value.audited.slice().reverse() : [];
});

watch(
  reversedAudited,
  (list) => {
    showInfo.value = new Array(list.length).fill(false);
  },
  { immediate: true }
);

function openExtendEta() {
  showExtendEta.value = true;
}

function closeExtendEta() {
  showExtendEta.value = false;
}

function openEstimatedWorkHoursModal() {
  showEstimatedWorkHoursModal.value = true;
}

function closeEstimatedWorkHoursModal() {
  showEstimatedWorkHoursModal.value = false;
}

function openCancelProjectModal() {
  showCancelProjectModal.value = true;
}

function closeCancelProjectModal() {
  showCancelProjectModal.value = false;
}

function toggleShowInfo(index) {
  showInfo.value[index] = !showInfo.value[index];
}
function expandBox(index) {
  toggleShowInfo(index);
}

function closePopUp() {
  isOpen.value = false;
  showInfo.value = [];
  emit("close");
}
function onOpened() {
  emit("open");
}
function onClosed() {
  //
}

function formatAuditBody(audit) {
  let auditBody;

  if (Object.keys(audit.changes || {}).length === 0) {
    auditBody = "<p>No hubo cambios</p>";
    auditBody += '<div class="wrap-value">' + (audit.comment || "") + "</div>";
  } else {
    auditBody =
      '<table class="table table-dark table-hover">' +
      "<thead>" +
      "<tr>" +
      "<td>Atributo</td>" +
      "<td>Antigüo</td>" +
      "<td>Nuevo</td>" +
      "</tr>" +
      "</thead>" +
      "<tbody>";

    Object.keys(audit.changes).forEach(function (key) {
      const value = audit.changes[key];
      if (value !== null) {
        if (typeof value === "object") {
          auditBody +=
            "<tr>" +
            '<td class="td-wrap">' +
            key +
            "</td>" +
            '<td class="td-wrap">' +
            (value[0] ?? "") +
            "</td>" +
            '<td class="td-wrap">' +
            (value[1] ?? "") +
            "</td>" +
            "</tr>";
        } else {
          auditBody +=
            "<tr>" +
            '<td class="td-wrap">' +
            key +
            "</td>" +
            "<td></td>" +
            '<td class="td-wrap">' +
            value +
            "</td>" +
            "</tr>";
        }
      }
    });

    auditBody +=
      "</tbody></table>" +
      '<div class="wrap-value">' +
      (audit.comment || "") +
      "</div>";
  }
  return auditBody;
}

async function fetchProject() {
  if (props.idProject) {
    store.dispatch("app/showLoading");
    try {
      await store.dispatch("fetchProject", props.idProject);
    } finally {
      store.dispatch("app/hideLoading");
    }
  }
}

watch(
  () => props.idProject,
  (val, old) => {
    if (val && val !== old) fetchProject();
  },
  { immediate: true }
);

watch(
  () => isOpen.value,
  (open) => {
    if (open && props.idProject) fetchProject();
  }
);

const openDriveFolder = () => {
  if (project?.value?.drive_root_id) {
    const url = `https://drive.google.com/drive/folders/${project.value.drive_root_id}`;
    window.open(url, "_blank");
  }
};
</script>

<style scoped>
.inline-title {
  width: 100%;
  align-items: center;
  display: flex;
  justify-content: space-between;
}

hr {
  width: 100%;
  border: none;
  border: 3px solid white;
  margin: 1em 0;
  padding: 0;
}

.contenedor {
  margin-top: 20px;
  width: 75%;
}

.line {
  margin-bottom: 20px;
  position: relative;
}

.line::before {
  content: "";
  position: absolute;
  width: 3px;
  height: 105%;
  background: #f46516;
  left: -10px;
  top: 10px;
}

.line::after {
  content: "";
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f46516;
  left: -19px;
  top: 0px;
}

.box {
  cursor: pointer;
  margin-left: 20px;
  padding: 20px;
  border-radius: 8px;
  background-color: #18161d;
  border-top: 4px solid #f46516;
  border-bottom: 0.1px solid #ffffff0a;
  border-left: 0.1px solid #ffffff0a;
  border-right: 0.1px solid #ffffff0a;
  max-height: 100px;
  min-height: 80px;
  transition: max-height 0.5s ease;
  overflow: auto;
}

.box.expanded {
  overflow: auto;
  max-height: 250px;
}

.abajos-enter-from,
.abajos-leave-to {
  opacity: 0;
}
.abajos-enter-active,
.abajos-leave-active {
  transition: all 0.2s ease;
}
</style>
